import { z } from 'zod';
/**
 * Receitas: a pessoa pede ao Secretário, em conversa normal ("abre uma receita de
 * bolo de fubá, vamos fazer agora, me guia"), e ele abre uma página estruturada —
 * ingredientes, modo de preparo passo a passo — e guia pela voz. As receitas ficam
 * salvas em Documentos; dá para abrir de novo, editar, apagar e compartilhar em PDF.
 * Não é "modo cozinha" nem ajuste de perfil: é conversa, a página é só o resultado.
 */
export const RECIPE_TITLE_MAX = 120;
export const RECIPE_LINE_MAX = 280;
export const RECIPE_INGREDIENTS_MAX = 60;
export const RECIPE_STEPS_MAX = 40;
export const RECIPE_TIPS_MAX = 12;
/** Receitas devolvidas na lista (as mais novas). */
export const RECIPE_LIST_MAX = 200;
export const RECIPE_DIFFICULTIES = ['facil', 'media', 'dificil'];
export const recipeDifficultySchema = z.enum(RECIPE_DIFFICULTIES);
/** Categoria só para a ilustração do cabeçalho; não classifica nada da pessoa. */
export const RECIPE_KINDS = [
    'bolo',
    'salgado',
    'doce',
    'pao',
    'massa',
    'carne',
    'salada',
    'sopa',
    'bebida',
    'outro',
];
export const recipeKindSchema = z.enum(RECIPE_KINDS);
const line = z.string().trim().min(1).max(RECIPE_LINE_MAX);
export const recipeIngredientSchema = z.object({
    /** O que é e quanto, numa linha ("2 xícaras de fubá", "goiabada a gosto"). */
    item: line,
    /** Agrupamento opcional ("massa", "cobertura"); vazio = sem grupo. */
    group: z.string().trim().max(60).default(''),
});
export const recipeStepSchema = z.object({
    text: line,
    /** Minutos deste passo, quando fizer sentido (forno, descanso); null = sem tempo. */
    minutes: z.number().int().min(0).max(1440).nullable().default(null),
});
/** O conteúdo da receita, como o modelo monta e como a página mostra. */
export const recipeBodySchema = z.object({
    title: z.string().trim().min(1).max(RECIPE_TITLE_MAX),
    kind: recipeKindSchema.default('outro'),
    /** Rende quanto ("12 fatias", "4 porções"); vazio quando não dá para dizer. */
    yield: z.string().trim().max(80).default(''),
    /** Tempo total em texto ("40 min", "1h20"); vazio quando não dá. */
    time: z.string().trim().max(60).default(''),
    difficulty: recipeDifficultySchema.default('facil'),
    ingredients: z.array(recipeIngredientSchema).min(1).max(RECIPE_INGREDIENTS_MAX),
    steps: z.array(recipeStepSchema).min(1).max(RECIPE_STEPS_MAX),
    tips: z.array(line).max(RECIPE_TIPS_MAX).default([]),
});
export const recipeSchema = recipeBodySchema.extend({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** Resumo para a lista (sem ingredientes/passos). */
export const recipeSummarySchema = z.object({
    id: z.uuid(),
    title: z.string(),
    kind: recipeKindSchema,
    yield: z.string(),
    time: z.string(),
    difficulty: recipeDifficultySchema,
    stepsCount: z.number().int().min(0),
    updatedAt: z.iso.datetime(),
});
export const RECIPE_KIND_LABELS = {
    bolo: 'Bolo',
    salgado: 'Salgado',
    doce: 'Doce',
    pao: 'Pão',
    massa: 'Massa',
    carne: 'Carne',
    salada: 'Salada',
    sopa: 'Sopa',
    bebida: 'Bebida',
    outro: 'Receita',
};
export const RECIPE_DIFFICULTY_LABELS = {
    facil: 'Fácil',
    media: 'Média',
    dificil: 'Difícil',
};
export const recipeListSchema = z.array(recipeSummarySchema);
